import React, { useEffect, useState, useRef } from "react";
import { Clock, ArrowRight, ChevronLeft, ChevronRight, UserPlus, KeyRound, ShieldCheck, FileSignature, Code2, Rocket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import PhotonLogo from "./PhotonLogo";
import { onboarding } from "../mock";

const ICONS = [UserPlus, KeyRound, ShieldCheck, FileSignature, Code2, Rocket];

const Onboarding = () => {
  const [ref, inView] = useReveal();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = onboarding.length;
  const touch = useRef({ x: 0, moved: false });

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((a) => (a + 1) % n), 3500);
    return () => clearInterval(t);
  }, [n, paused]);

  const rel = (i) => {
    let r = i - active;
    if (r > n / 2) r -= n;
    if (r < -n / 2) r += n;
    return r;
  };

  const go = (dir) => setActive((a) => (a + dir + n) % n);

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, moved: false };
    setPaused(true);
  };
  const onTouchMove = (e) => {
    if (Math.abs(e.touches[0].clientX - touch.current.x) > 8) touch.current.moved = true;
  };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    setPaused(false);
  };

  return (
    <section id="onboarding" className="py-20 md:py-28 bg-white overflow-hidden">
      <div ref={ref} className="pp-container grid lg:grid-cols-[0.85fr_1.15fr] gap-10 items-center">
        {/* Left text */}
        <div className={`pp-reveal ${inView ? "pp-in" : ""}`}>
          <div className="flex items-center gap-2 mb-4">
            <PhotonLogo variant="dark" showTagline={false} className="scale-90 origin-left" />
          </div>
          <span className="inline-block text-[#0D1B2A]/60 font-semibold uppercase tracking-[0.2em] text-sm">
            Onboard Fast. Integrate Smarter.
          </span>
          <h2 className="mt-4 text-4xl md:text-5xl font-extrabold leading-[1.1] text-[#0D1B2A]">
            Join in Minutes, Transact <span className="pp-grad-text">with Confidence</span>
          </h2>
          <button
            onClick={() => navigate("/signup")}
            className="group mt-8 inline-flex items-center gap-3 text-white font-semibold pl-7 pr-3 py-3.5 rounded-full transition-transform hover:scale-[1.03]"
            style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}
          >
            Become a PhotonPE Partner
            <span className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center group-hover:translate-x-1 transition-transform">
              <ArrowRight size={16} />
            </span>
          </button>

          {/* Arrows + dots */}
          <div className="mt-9 flex items-center gap-4">
            <button onClick={() => go(-1)} className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-[#0D1B2A] hover:bg-[#0D1B2A] hover:text-white transition-colors">
              <ChevronLeft size={18} />
            </button>
            <button onClick={() => go(1)} className="w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-[#0D1B2A] hover:bg-[#0D1B2A] hover:text-white transition-colors">
              <ChevronRight size={18} />
            </button>
            <div className="flex gap-1.5 ml-2">
              {onboarding.map((_, i) => (
                <button key={i} onClick={() => setActive(i)} className="h-2 rounded-full transition-all" style={{ width: i === active ? 26 : 8, background: i === active ? "#06B6D4" : "#cdd8e5" }} />
              ))}
            </div>
          </div>
        </div>

        {/* Coverflow slider */}
        <div
          className={`relative h-[500px] pp-reveal ${inView ? "pp-in" : ""}`}
          style={{ animationDelay: "0.15s", touchAction: "pan-y" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Floating step number */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-0 pointer-events-none select-none">
            <span
              key={active}
              className="text-[130px] font-extrabold leading-none pp-reveal pp-in"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                background: "linear-gradient(180deg, #0EA5E9 0%, rgba(46,143,212,0.15) 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                opacity: 0.9,
              }}
            >
              {onboarding[active].n}
            </span>
          </div>

          {/* Cards */}
          <div className="absolute inset-0 top-16 flex items-center justify-center">
            {onboarding.map((step, i) => {
              const r = rel(i);
              if (Math.abs(r) > 1) return null;
              const isActive = r === 0;
              const Icon = ICONS[i % ICONS.length];
              return (
                <div
                  key={step.n}
                  onClick={() => {
                    if (touch.current.moved) return;
                    isActive ? navigate("/signup") : setActive(i);
                  }}
                  className="absolute cursor-pointer"
                  style={{
                    width: 340,
                    transform: `translateX(calc(${r * 66}% + ${r * 20}px)) scale(${isActive ? 1 : 0.86})`,
                    opacity: isActive ? 1 : 0.4,
                    zIndex: 10 - Math.abs(r),
                    transition: "all 0.6s cubic-bezier(0.22,1,0.36,1)",
                  }}
                >
                  {/* Gradient border wrapper */}
                  <div
                    className="rounded-[26px] p-[2px]"
                    style={{
                      background: isActive
                        ? "linear-gradient(135deg, #22c55e, #0EA5E9)"
                        : "#eef3f9",
                    }}
                  >
                    <div className="rounded-[24px] bg-white px-7 py-9 text-center shadow-[0_30px_60px_-30px_rgba(13,27,42,0.35)]">
                      {/* Illustration */}
                      <div
                        className="w-28 h-28 mx-auto rounded-3xl flex items-center justify-center"
                        style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.10), rgba(34,197,94,0.10))" }}
                      >
                        <Icon size={46} strokeWidth={1.6} className="text-[#0EA5E9]" />
                      </div>
                      <h3 className="mt-6 text-2xl font-extrabold text-[#0D1B2A]">{step.title}</h3>
                      <p className="mt-2 text-[15px] text-[#475569] leading-relaxed min-h-[48px]">{step.desc}</p>
                      <div className="mt-6 inline-flex items-center gap-2 bg-[#F0FDFA] text-[#0EA5E9] font-semibold text-sm px-6 py-3 rounded-full w-full justify-center">
                        <Clock size={16} /> {step.time}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Onboarding;
